import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Copy, KeyRound, Loader2, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BrandSelect } from "@/components/features/brands/BrandSelect";
import { listBrands } from "@/services/brandService";
import { brandKeys } from "@/lib/queryKeys";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { cn } from "@/lib/utils";
import { tenantKeys } from "@/lib/queryKeys";
import { apiBaseUrl } from "@/services/apiClient";
import {
  createTenantApiKey,
  getApiIntegrationUsage,
  getInvoiceWebhookSettings,
  listTenantApiKeys,
  patchInvoiceWebhookSettings,
  revokeTenantApiKey,
  setTenantApiKeyBrand,
  testInvoiceWebhook,
  type TenantApiKeyRow,
} from "@/services/authService";
import { isFreePlan } from "@/components/shared/PaidPlanRoute";

type SettingsApiIntegrationCardProps = {
  planId: string | null | undefined;
};

export function SettingsApiIntegrationCard({
  planId,
}: SettingsApiIntegrationCardProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isOnFreePlan = isFreePlan(planId);

  const [createOpen, setCreateOpen] = useState(false);
  const [newEnv, setNewEnv] = useState<"test" | "live">("test");
  const [newName, setNewName] = useState("");
  const [secretReveal, setSecretReveal] = useState<string | null>(null);
  const [revokeKey, setRevokeKey] = useState<TenantApiKeyRow | null>(null);
  const [newBrandId, setNewBrandId] = useState<string | null>(null);
  // Only to decide whether the marca column is worth a table header at all.
  const { data: brandList = [] } = useQuery({
    queryKey: brandKeys.list(),
    queryFn: listBrands,
  });
  const hasBrands = brandList.length > 0;
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");

  // Firma de webhooks state
  const [signingPasteOpen, setSigningPasteOpen] = useState(false);
  const [signingPasteValue, setSigningPasteValue] = useState("");
  const [signingReveal, setSigningReveal] = useState<string | null>(null);

  const { data: webhookSettings, isLoading: webhookLoading } = useQuery({
    queryKey: tenantKeys.invoiceWebhook(),
    queryFn: getInvoiceWebhookSettings,
    enabled: !isOnFreePlan,
  });

  useEffect(() => {
    if (webhookSettings) {
      setWebhookUrl(webhookSettings.url ?? "");
      setWebhookSecret("");
    }
  }, [webhookSettings]);

  const { data: keys = [], isLoading: keysLoading } = useQuery({
    queryKey: tenantKeys.apiKeys(),
    queryFn: listTenantApiKeys,
    enabled: !isOnFreePlan,
  });

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: tenantKeys.apiIntegrationUsage(),
    queryFn: getApiIntegrationUsage,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createTenantApiKey({
        environment: newEnv,
        name: newName.trim() || undefined,
        brandId: newBrandId,
      }),
    onSuccess: (data) => {
      setSecretReveal(data.secret);
      setCreateOpen(false);
      setNewName("");
      setNewBrandId(null);
      void queryClient.invalidateQueries({ queryKey: tenantKeys.apiKeys() });
      toast.success(t("settings.apiIntegration.createSuccess"));
    },
    onError: (err: unknown) => {
      const msg =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "message" in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : t("settings.apiIntegration.createError");
      toast.error(msg);
    },
  });

  const generateSigningSecret = (): string => {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    const env = import.meta.env.MODE === "production" ? "live" : "test";
    return `fws_${env}_${hex}`;
  };

  const saveSigningSecretMutation = useMutation({
    mutationFn: (secret: string) => patchInvoiceWebhookSettings({ secret }),
    onSuccess: (_data, secret) => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.invoiceWebhook() });
      setSigningReveal(secret);
      setSigningPasteOpen(false);
      setSigningPasteValue("");
      toast.success(t("settings.apiIntegration.webhookSigningSaveSuccess"));
    },
    onError: () => toast.error(t("settings.apiIntegration.webhookSigningSaveError")),
  });

  const rotateSigningSecretMutation = useMutation({
    mutationFn: () => {
      const secret = generateSigningSecret();
      return patchInvoiceWebhookSettings({ secret }).then(() => secret);
    },
    onSuccess: (secret) => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.invoiceWebhook() });
      setSigningReveal(secret);
      toast.success(t("settings.apiIntegration.webhookSigningRotateSuccess"));
    },
    onError: () => toast.error(t("settings.apiIntegration.webhookSigningRotateError")),
  });

  const removeSigningSecretMutation = useMutation({
    mutationFn: () => patchInvoiceWebhookSettings({ secret: null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.invoiceWebhook() });
      setSigningReveal(null);
      toast.success(t("settings.apiIntegration.webhookSigningRemoveSuccess"));
    },
    onError: () => toast.error(t("settings.apiIntegration.webhookSigningRemoveError")),
  });

  const handleGenerateSecret = (): void => {
    const secret = generateSigningSecret();
    saveSigningSecretMutation.mutate(secret);
  };

  const handlePasteSave = (): void => {
    if (signingPasteValue.trim().length < 32) return;
    saveSigningSecretMutation.mutate(signingPasteValue.trim());
  };

  const resetWebhookForm = (): void => {
    setWebhookUrl(webhookSettings?.url ?? "");
    setWebhookSecret("");
  };

  const saveWebhookMutation = useMutation({
    mutationFn: () => {
      const body: { url?: string | null; secret?: string } = {
        url: webhookUrl.trim() === "" ? null : webhookUrl.trim(),
      };
      if (webhookSecret.trim() !== "") {
        body.secret = webhookSecret.trim();
      }
      return patchInvoiceWebhookSettings(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.invoiceWebhook() });
      setWebhookSecret("");
      toast.success(t("settings.apiIntegration.webhookSaveSuccess"));
    },
    onError: () => toast.error(t("settings.apiIntegration.webhookSaveError")),
  });

  const testWebhookMutation = useMutation({
    mutationFn: testInvoiceWebhook,
    onSuccess: (data) => {
      toast.success(
        t("settings.apiIntegration.webhookTestSuccess", { status: data.httpStatus })
      );
    },
    onError: () => toast.error(t("settings.apiIntegration.webhookTestError")),
  });

  const isWebhookSecretMissingForUrl =
    webhookUrl.trim() !== "" &&
    webhookSecret.trim() === "" &&
    !webhookSettings?.secretConfigured;

  const handleSaveWebhook = (): void => {
    if (isWebhookSecretMissingForUrl) {
      toast.error(t("settings.apiIntegration.webhookSecretRequired"));
      return;
    }
    saveWebhookMutation.mutate();
  };

  const handleTestWebhook = (): void => {
    if (isWebhookSecretMissingForUrl) {
      toast.error(t("settings.apiIntegration.webhookSecretRequired"));
      return;
    }
    testWebhookMutation.mutate();
  };

  /** Changing the marca on an EXISTING key: the secret is shown once, so
   *  re-issuing a key just to switch brand would force whoever holds it to
   *  redeploy. */
  const brandMutation = useMutation({
    mutationFn: ({ id, brandId }: { id: string; brandId: string | null }) =>
      setTenantApiKeyBrand(id, brandId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.apiKeys() });
      toast.success(t("settings.apiIntegration.brandSaved", "Marca actualizada."));
    },
    onError: () =>
      toast.error(
        t("settings.apiIntegration.brandError", "No pudimos actualizar la marca.")
      ),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeTenantApiKey(id),
    onSuccess: () => {
      setRevokeKey(null);
      void queryClient.invalidateQueries({ queryKey: tenantKeys.apiKeys() });
      toast.success(t("settings.apiIntegration.revokeSuccess"));
    },
    onError: () => toast.error(t("settings.apiIntegration.revokeError")),
  });

  const apiBase = apiBaseUrl.replace(/\/$/, "");
  const apiEndpoints: Array<{
    method: "POST" | "GET";
    path: string;
    descriptionKey:
      | "endpointCreateInvoice"
      | "endpointGetInvoice"
      | "endpointCreditNote"
      | "endpointPdf";
  }> = [
    { method: "POST", path: `${apiBase}/api/v1/invoices`, descriptionKey: "endpointCreateInvoice" },
    {
      method: "GET",
      path: `${apiBase}/api/v1/invoices/:id`,
      descriptionKey: "endpointGetInvoice",
    },
    {
      method: "POST",
      path: `${apiBase}/api/v1/invoices/:id/credit-note`,
      descriptionKey: "endpointCreditNote",
    },
    {
      method: "GET",
      path: `${apiBase}/api/v1/invoices/:id/pdf`,
      descriptionKey: "endpointPdf",
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-foreground">{t("settings.apiIntegration.title")}</CardTitle>
          <CardDescription>{t("settings.apiIntegration.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isOnFreePlan && (
            <Alert>
              <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span>{t("settings.apiIntegration.freePlanBlocked")}</span>
                <Button
                  asChild
                  variant="secondary"
                  size="sm"
                  className="shrink-0 transition-colors duration-150"
                >
                  <Link to="/settings?tab=billing">{t("settings.apiIntegration.upgradeCta")}</Link>
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <Collapsible defaultOpen={false} className="group rounded-lg border bg-muted/30">
            <CollapsibleTrigger
              type="button"
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-lg p-4 text-left",
                "transition-colors duration-150 hover:bg-muted/40",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              )}
            >
              <span className="text-sm font-medium text-foreground">
                {t("settings.apiIntegration.endpointsTitle")}
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
                aria-hidden
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-4 border-t border-border px-4 pb-4 pt-3">
                <p className="text-xs text-muted-foreground">{t("settings.apiIntegration.authHint")}</p>
                <ul className="list-none space-y-4 p-0" role="list">
                  {apiEndpoints.map((row) => (
                    <li key={row.path} className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={row.method === "POST" ? "default" : "secondary"}
                          className="shrink-0 font-mono text-xs transition-colors duration-150"
                        >
                          {row.method}
                        </Badge>
                        <code className="break-all text-xs text-muted-foreground">{row.path}</code>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t(`settings.apiIntegration.${row.descriptionKey}`)}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground">
              {t("settings.apiIntegration.usageTitle")}
            </h3>
            {usageLoading || !usage ? (
              <div className="flex min-h-[48px] items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ul className="grid gap-2 text-sm sm:grid-cols-2">
                <li className="flex justify-between gap-2 rounded-md border bg-background px-3 py-2 transition-colors duration-150">
                  <span className="text-muted-foreground">{t("settings.apiIntegration.usagePeriod")}</span>
                  <span className="font-medium text-foreground">
                    {`${usage.calendarYear}-${String(usage.calendarMonth).padStart(2, "0")}`}
                  </span>
                </li>
                <li className="flex justify-between gap-2 rounded-md border bg-background px-3 py-2 transition-colors duration-150">
                  <span className="text-muted-foreground">{t("settings.apiIntegration.usageTotal")}</span>
                  <span className="font-medium text-foreground">{usage.totalEmitted}</span>
                </li>
                <li className="flex justify-between gap-2 rounded-md border bg-background px-3 py-2 transition-colors duration-150">
                  <span className="text-muted-foreground">{t("settings.apiIntegration.usageApi")}</span>
                  <span className="font-medium text-foreground">{usage.emittedViaApi}</span>
                </li>
                <li className="flex justify-between gap-2 rounded-md border bg-background px-3 py-2 transition-colors duration-150">
                  <span className="text-muted-foreground">{t("settings.apiIntegration.usageLimit")}</span>
                  <span className="font-medium text-foreground">
                    {usage.maxCfesPerMonth == null
                      ? t("settings.apiIntegration.unlimited")
                      : usage.maxCfesPerMonth}
                  </span>
                </li>
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {!isOnFreePlan && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">
                {t("settings.apiIntegration.webhookSigningTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {webhookLoading ? (
                <div className="flex min-h-[80px] items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : webhookSettings?.secretConfigured ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    {t("settings.apiIntegration.webhookSigningConfigured")}
                  </p>
                  {signingReveal && (
                    <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs font-medium text-amber-800">
                        {t("settings.apiIntegration.webhookSigningOnceWarning")}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <code className="flex-1 break-all rounded-md border bg-background px-3 py-2 text-xs">
                          {signingReveal}
                        </code>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="shrink-0 transition-colors duration-150"
                          onClick={() => {
                            void navigator.clipboard.writeText(signingReveal);
                            toast.success(t("settings.apiIntegration.webhookSigningCopied"));
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={rotateSigningSecretMutation.isPending}
                      className="transition-colors duration-150"
                      onClick={() => rotateSigningSecretMutation.mutate()}
                    >
                      {rotateSigningSecretMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("settings.apiIntegration.webhookSigningRotate")}
                        </>
                      ) : (
                        t("settings.apiIntegration.webhookSigningRotate")
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={
                        removeSigningSecretMutation.isPending ||
                        Boolean(webhookSettings?.url)
                      }
                      title={
                        webhookSettings?.url
                          ? t("settings.apiIntegration.webhookSigningRemoveDisabledTooltip")
                          : undefined
                      }
                      className="text-destructive transition-colors duration-150 hover:text-destructive disabled:opacity-50"
                      onClick={() => removeSigningSecretMutation.mutate()}
                    >
                      {removeSigningSecretMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("settings.apiIntegration.webhookSigningRemove")}
                        </>
                      ) : (
                        t("settings.apiIntegration.webhookSigningRemove")
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {t("settings.apiIntegration.webhookSigningDescription")}
                  </p>
                  {signingReveal ? (
                    <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs font-medium text-amber-800">
                        {t("settings.apiIntegration.webhookSigningOnceWarning")}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <code className="flex-1 break-all rounded-md border bg-background px-3 py-2 text-xs">
                          {signingReveal}
                        </code>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="shrink-0 transition-colors duration-150"
                          onClick={() => {
                            void navigator.clipboard.writeText(signingReveal);
                            toast.success(t("settings.apiIntegration.webhookSigningCopied"));
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : signingPasteOpen ? (
                    <div className="space-y-2">
                      <Label htmlFor="signing-secret-paste">
                        {t("settings.apiIntegration.webhookSigningPasteLabel")}
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        <Input
                          id="signing-secret-paste"
                          type="password"
                          autoComplete="new-password"
                          value={signingPasteValue}
                          onChange={(e) => setSigningPasteValue(e.target.value)}
                          className="flex-1 transition-colors duration-150"
                        />
                        <Button
                          type="button"
                          disabled={
                            signingPasteValue.trim().length < 32 ||
                            saveSigningSecretMutation.isPending
                          }
                          className="shrink-0 transition-colors duration-150"
                          onClick={handlePasteSave}
                        >
                          {saveSigningSecretMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {t("settings.apiIntegration.webhookSigningSave")}
                            </>
                          ) : (
                            t("settings.apiIntegration.webhookSigningSave")
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="shrink-0 transition-colors duration-150"
                          onClick={() => {
                            setSigningPasteOpen(false);
                            setSigningPasteValue("");
                          }}
                        >
                          {t("settings.apiIntegration.cancel")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        disabled={saveSigningSecretMutation.isPending}
                        className="transition-colors duration-150"
                        onClick={handleGenerateSecret}
                      >
                        {saveSigningSecretMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t("settings.apiIntegration.webhookSigningGenerate")}
                          </>
                        ) : (
                          t("settings.apiIntegration.webhookSigningGenerate")
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="transition-colors duration-150"
                        onClick={() => setSigningPasteOpen(true)}
                      >
                        {t("settings.apiIntegration.webhookSigningPaste")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">
                {t("settings.apiIntegration.webhookTitle")}
              </CardTitle>
              <CardDescription>{t("settings.apiIntegration.webhookDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {webhookLoading ? (
                <div className="flex min-h-[120px] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="invoice-webhook-url">{t("settings.apiIntegration.webhookUrlLabel")}</Label>
                    <Input
                      id="invoice-webhook-url"
                      type="url"
                      autoComplete="off"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder={t("settings.apiIntegration.webhookUrlPlaceholder")}
                      className="transition-colors duration-150"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice-webhook-secret">{t("settings.apiIntegration.webhookSecretLabel")}</Label>
                    <Input
                      id="invoice-webhook-secret"
                      type="password"
                      autoComplete="new-password"
                      value={webhookSecret}
                      onChange={(e) => setWebhookSecret(e.target.value)}
                      placeholder={
                        webhookSettings?.secretConfigured
                          ? t("settings.apiIntegration.webhookSecretPlaceholderKeep")
                          : t("settings.apiIntegration.webhookSecretPlaceholder")
                      }
                      className="transition-colors duration-150"
                    />
                    <p className="text-xs text-muted-foreground">
                      {webhookSettings?.secretConfigured
                        ? t("settings.apiIntegration.webhookSecretHintWhenSet")
                        : t("settings.apiIntegration.webhookSecretHintWhenUnset")}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-row flex-wrap items-center justify-between gap-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={resetWebhookForm}
                className="transition-colors duration-150"
              >
                {t("settings.apiIntegration.cancel")}
              </Button>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    webhookUrl.trim() === "" ||
                    isWebhookSecretMissingForUrl ||
                    testWebhookMutation.isPending ||
                    webhookLoading
                  }
                  className="transition-colors duration-150"
                  onClick={handleTestWebhook}
                >
                  {testWebhookMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("settings.apiIntegration.webhookTesting")}
                    </>
                  ) : (
                    t("settings.apiIntegration.webhookTest")
                  )}
                </Button>
                <Button
                  type="button"
                  disabled={saveWebhookMutation.isPending || webhookLoading}
                  className="transition-colors duration-150"
                  onClick={handleSaveWebhook}
                >
                  {saveWebhookMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("settings.apiIntegration.webhookSaving")}
                    </>
                  ) : (
                    t("settings.apiIntegration.webhookSave")
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-foreground">{t("settings.apiIntegration.keysTitle")}</CardTitle>
            <CardDescription>{t("settings.apiIntegration.keysDescription")}</CardDescription>
          </div>
          <Button
            type="button"
            size="sm"
            disabled={isOnFreePlan}
            onClick={() => setCreateOpen(true)}
            className="transition-colors duration-150"
          >
            <KeyRound className="mr-2 h-4 w-4" />
            {t("settings.apiIntegration.createKey")}
          </Button>
        </CardHeader>
        <CardContent>
          {isOnFreePlan ? null : keysLoading ? (
            <div className="flex min-h-[120px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("settings.apiIntegration.noKeys")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("settings.apiIntegration.colEnvironment")}</TableHead>
                  <TableHead>{t("settings.apiIntegration.colName")}</TableHead>
                  <TableHead>{t("settings.apiIntegration.colPrefix")}</TableHead>
                  {hasBrands && (
                    <TableHead>
                      {t("settings.apiIntegration.colBrand", "Marca")}
                    </TableHead>
                  )}
                  <TableHead>{t("settings.apiIntegration.colStatus")}</TableHead>
                  <TableHead className="w-[100px] text-right">{t("settings.apiIntegration.colActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell>
                      <Badge variant={k.environment === "test" ? "secondary" : "default"}>
                        {k.environment === "test"
                          ? t("settings.apiIntegration.envTest")
                          : t("settings.apiIntegration.envLive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-foreground">{k.name ?? "—"}</TableCell>
                    <TableCell>
                      <code className="text-xs">{k.keyPrefix}</code>
                    </TableCell>
                    {hasBrands && (
                      <TableCell>
                        {/* Editable in place: this is the setting that gives a
                            product's own backend its own identity, and it is
                            the reason a key would need changing at all. */}
                        <BrandSelect
                          value={k.brandId}
                          onChange={(brandId) =>
                            brandMutation.mutate({ id: k.id, brandId })
                          }
                          disabled={
                            Boolean(k.revokedAt) || brandMutation.isPending
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      {k.revokedAt ? (
                        <span className="text-muted-foreground">{t("settings.apiIntegration.revoked")}</span>
                      ) : (
                        <span className="text-foreground">{t("settings.apiIntegration.active")}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={Boolean(k.revokedAt)}
                        className="text-destructive hover:text-destructive transition-colors duration-150"
                        aria-label={t("settings.apiIntegration.revoke")}
                        onClick={() => setRevokeKey(k)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("settings.apiIntegration.createDialogTitle")}</DialogTitle>
            <DialogDescription>{t("settings.apiIntegration.createDialogDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="api-key-env">{t("settings.apiIntegration.fieldEnvironment")}</Label>
              <Select
                value={newEnv}
                onValueChange={(v) => setNewEnv(v as "test" | "live")}
              >
                <SelectTrigger id="api-key-env" className="transition-colors duration-150">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">{t("settings.apiIntegration.envTestOption")}</SelectItem>
                  <SelectItem value="live">{t("settings.apiIntegration.envLiveOption")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hasBrands && (
              <div className="space-y-2">
                <Label htmlFor="api-key-brand">
                  {t("settings.apiIntegration.fieldBrand", "Marca")}
                </Label>
                <BrandSelect
                  id="api-key-brand"
                  value={newBrandId}
                  onChange={setNewBrandId}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "settings.apiIntegration.fieldBrandHelp",
                    "Las facturas creadas con esta clave salen con esta marca, salvo que el pedido indique otra."
                  )}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="api-key-name">{t("settings.apiIntegration.fieldName")}</Label>
              <Input
                id="api-key-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("settings.apiIntegration.fieldNamePlaceholder")}
                maxLength={128}
                className="transition-colors duration-150"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              {t("settings.apiIntegration.cancel")}
            </Button>
            <Button
              type="button"
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("settings.apiIntegration.creating")}
                </>
              ) : (
                t("settings.apiIntegration.createSubmit")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={secretReveal != null} onOpenChange={(o) => !o && setSecretReveal(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("settings.apiIntegration.secretTitle")}</DialogTitle>
            <DialogDescription>{t("settings.apiIntegration.secretDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <code className="flex-1 break-all rounded-md border bg-muted px-3 py-2 text-xs">
                {secretReveal}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0 transition-colors duration-150"
                onClick={() => {
                  if (secretReveal) {
                    void navigator.clipboard.writeText(secretReveal);
                    toast.success(t("settings.apiIntegration.copied"));
                  }
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setSecretReveal(null)}>
              {t("settings.apiIntegration.secretDone")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={revokeKey != null}
        onOpenChange={(o) => !o && setRevokeKey(null)}
        title={t("settings.apiIntegration.revokeConfirmTitle")}
        description={t("settings.apiIntegration.revokeConfirmDescription")}
        confirmText={t("settings.apiIntegration.revokeConfirm")}
        cancelText={t("settings.apiIntegration.cancel")}
        loadingText={t("settings.apiIntegration.revoking")}
        variant="destructive"
        isLoading={revokeMutation.isPending}
        onConfirm={() => {
          if (revokeKey) revokeMutation.mutate(revokeKey.id);
        }}
      />
    </div>
  );
}
